// React imports
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

// Types
import { Invoice, Promocode, SavePromocode } from '@spike/promocode-model';
import { RootState } from 'store';

// Actions
import {
    fetchPromocodesThunk,
    savePromocodeThunk,
    fetchPromoCodeRedeemsThunk,
    PromocodesStatus,
    clearPromocodeError,
    clearPromocodeSuccess
} from '@spike/promocodes-action';
import { showErrorThunk } from '@spike/notifications-action';

// Hooks
import { useApiClientWrapper } from 'hooks';

// Utils
import {
    convertPromocodeToSavePromocode,
    convertPromocodeToActiveSavePromocode,
    convertPromocodeToInactiveSavePromocode
} from './Utils';

interface PromoCodesContextProps {
    // Data
    promoCodes: Array<Promocode>;
    redeems: Array<Invoice>;
    isLoading: boolean;

    isFirstRender: boolean;
    error?: string;
    hasError: boolean;
    status: PromocodesStatus;

    // Promo Codes Pagination
    size: number;
    page: number;
    totalPages: number;
    hasMorePages: boolean;
    setSize: (size: number) => void;
    setPage: (page: number) => void;

    // Redeems Pagination
    redeemsSize: number;
    redeemsPage: number;
    redeemsTotalPages: number;
    redeemsHasMorePages: boolean;
    setRedeemsSize: (size: number) => void;
    setRedeemsPage: (page: number) => void;

    // Redeems Search
    redeemsSearchText: string;
    setRedeemsSearchText: (text: string) => void;

    // Filtering
    selectedFilter: string;
    filterChangeHandler: (filter: string) => void;
    searchText: string;
    searchTextChangeHandler: (text: string) => void;

    // Form state
    showAddPromoCode: boolean;
    editPromoCode: SavePromocode | null;
    isConfigurationLocked: boolean;
    openAddForm: () => void;
    openEditForm: (promoCode: Promocode) => void;
    closeForm: () => void;

    // Actions
    savePromoCode: (promoCode: SavePromocode) => Promise<void>;
    activatePromoCode: (promoCode: Promocode) => Promise<void>;
    deactivatePromoCode: (promoCode: Promocode) => Promise<void>;
    loadMorePromoCodes: () => void;
    refreshPromoCodes: () => void;
    clearError: () => void;
    clearSuccess: () => void;

    // Usage modal
    showUsage: Promocode | null;
    setShowUsage: (promoCode: Promocode | null) => void;
    openUsageModal: (promoCode: Promocode) => void;
    closeUsageModal: () => void;

    // Redeem data
    fetchPromoCodeRedeems: (
        promocodeId: number,
        page?: number,
        pageSize?: number,
        searchText?: string
    ) => Promise<void>;
}

const PromoCodesContext = React.createContext<PromoCodesContextProps | null>(null);

interface PromoCodesProviderProps {
    children: React.ReactNode;
}

export interface FormErrorState {
    name: boolean;
    value: boolean;
    olb: boolean;
    startAt: boolean;
    endAt: boolean;
    endAtEnabled: boolean;
}

type PromocodesApiClient = Parameters<typeof fetchPromocodesThunk>[0];

export const PromoCodesProvider = ({ children }: PromoCodesProviderProps) => {
    const apiClientWrapper = useApiClientWrapper() as unknown as PromocodesApiClient;
    const dispatch = useDispatch();

    const { promocodes, pagination, error, status, redeems, redeemsPagination } = useSelector(
        (state: RootState) => state.promocodes
    );

    const [loading, setLoading] = React.useState({ promoCodes: true, initialLoading: true });
    const [pageInfo, setPageInfo] = React.useState({
        searchText: '',
        page: 1,
        pageSize: 10,
        status: 'active'
    });

    const [showAddPromoCode, setShowAddPromoCode] = React.useState<boolean>(false);
    const [editPromoCode, setEditPromoCode] = React.useState<SavePromocode | null>(null);
    const [isConfigurationLocked, setIsConfigurationLocked] = React.useState(false);
    const [showUsage, setShowUsage] = React.useState<Promocode | null>(null);
    const [redeemsSearchText, setRedeemsSearchText] = React.useState<string>('');

    // Simple fetch function
    const fetchPromoCodes = () => {
        dispatch(
            fetchPromocodesThunk(
                apiClientWrapper,
                pageInfo.page,
                pageInfo.pageSize,
                pageInfo.searchText,
                pageInfo.status
            )
        );
    };

    // Handle errors - dispatch error thunk and keep form open
    const hasError = status === PromocodesStatus.Error;
    React.useEffect(() => {
        if (hasError && error) {
            if (error.includes('name')) {
                dispatch(showErrorThunk('Promo code name already exists'));
            } else if (error.includes('date')) {
                dispatch(showErrorThunk('Invalid date range'));
            } else {
                dispatch(showErrorThunk(error));
            }
        }
    }, [hasError, error, dispatch]);

    const currentPagePromoCodes = React.useMemo(() => {
        // Apply client-side filtering to handle updated data correctly
        let filtered = promocodes;

        // Apply status filter
        if (pageInfo.status && pageInfo.status !== 'all') {
            const isActive = pageInfo.status === 'active';
            filtered = filtered.filter(promo => promo.active === isActive);
        }

        // Apply search filter
        if (pageInfo.searchText) {
            const searchLower = pageInfo.searchText.toLowerCase();
            filtered = filtered.filter(promo => promo.name.toLowerCase().includes(searchLower));
        }

        return filtered;
    }, [promocodes, pageInfo.status, pageInfo.searchText]);

    const hasMorePages = React.useMemo(() => {
        return pagination ? pageInfo.page < pagination.totalPages : false;
    }, [pagination, pageInfo.page]);

    // Redeems pagination calculations
    const redeemsHasMorePages = React.useMemo(() => {
        return redeemsPagination ? redeemsPagination.currentPage < redeemsPagination.totalPages : false;
    }, [redeemsPagination]);

    React.useEffect(() => {
        fetchPromoCodes();
    }, [pageInfo]);

    // Refetch when save operations complete successfully
    React.useEffect(() => {
        if (status === PromocodesStatus.SaveSuccess) {
            fetchPromoCodes();
        }
    }, [status, fetchPromoCodes]);

    // Handle status changes for promocodes and redeems
    React.useEffect(() => {
        if (status === PromocodesStatus.FetchSuccess || status === PromocodesStatus.Error) {
            setLoading(prev => ({ ...prev, promoCodes: false, initialLoading: false }));
        }
        if (status === PromocodesStatus.Fetching) {
            setLoading(prev => ({ ...prev, promoCodes: true }));
        }
    }, [status]);

    const savePromoCode = React.useCallback(
        async (promoCode: SavePromocode): Promise<void> => {
            await dispatch(savePromocodeThunk(apiClientWrapper, promoCode));
        },
        [apiClientWrapper, dispatch]
    );

    const activatePromoCode = React.useCallback(
        async (promoCode: Promocode): Promise<void> => {
            const savePromocode = convertPromocodeToActiveSavePromocode(promoCode);
            await dispatch(savePromocodeThunk(apiClientWrapper, savePromocode));
        },
        [apiClientWrapper, dispatch]
    );

    const deactivatePromoCode = React.useCallback(
        async (promoCode: Promocode): Promise<void> => {
            const savePromocode = convertPromocodeToInactiveSavePromocode(promoCode);
            await dispatch(savePromocodeThunk(apiClientWrapper, savePromocode));
        },
        [apiClientWrapper, dispatch]
    );

    const loadMorePromoCodes = React.useCallback(() => {
        if (hasMorePages && !loading.promoCodes) {
            setPageInfo(prev => ({ ...prev, page: prev.page + 1 }));
        }
    }, [hasMorePages, loading.promoCodes]);

    const refreshPromoCodes = React.useCallback(() => {
        setPageInfo(prev => ({ ...prev, page: 1 }));
    }, []);

    const clearError = React.useCallback(() => {
        dispatch(clearPromocodeError());
    }, [dispatch]);

    const clearSuccess = React.useCallback(() => {
        dispatch(clearPromocodeSuccess());
    }, [dispatch]);

    const fetchPromoCodeRedeems = React.useCallback(
        async (promocodeId: number, page = 1, pageSize = 10, searchText = ''): Promise<void> => {
            try {
                await dispatch(
                    fetchPromoCodeRedeemsThunk(apiClientWrapper, String(promocodeId), page, pageSize, searchText)
                );
            } catch (error) {
                // Error is already handled in the thunk
                console.error('Failed to fetch promo code redeems:', error);
            }
        },
        [apiClientWrapper, dispatch]
    );

    /*
     * ---- Event Handlers ----
     */

    const filterChangeHandler = React.useCallback((status: string) => {
        setPageInfo(prev => ({ ...prev, status, page: 1 }));
    }, []);

    const searchTextChangeHandler = useDebouncedCallback((text: string) => {
        setPageInfo(prev => ({ ...prev, searchText: text, page: 1 }));
    }, 500);

    const openAddForm = React.useCallback(() => {
        clearSuccess(); // Clear any previous SaveSuccess status
        setShowAddPromoCode(true);
        setEditPromoCode(null);
        setIsConfigurationLocked(false);
    }, [clearSuccess]);

    const openEditForm = React.useCallback(
        (promoCode: Promocode) => {
            clearSuccess(); // Clear any previous SaveSuccess status
            const savePromocode = convertPromocodeToSavePromocode(promoCode);
            setEditPromoCode(savePromocode);
            setShowAddPromoCode(false);
            // Used codes stay locked except Redeem Online.
            setIsConfigurationLocked(promoCode.totalRedeems > 0);
        },
        [clearSuccess]
    );

    const closeForm = React.useCallback(() => {
        setShowAddPromoCode(false);
        setEditPromoCode(null);
        setIsConfigurationLocked(false);
    }, []);

    const openUsageModal = React.useCallback(
        (promoCode: Promocode) => {
            setShowUsage(promoCode);
            // Reset search when opening usage modal
            setRedeemsSearchText('');
            // Automatically fetch redeems when opening usage modal
            fetchPromoCodeRedeems(promoCode.id);
        },
        [fetchPromoCodeRedeems]
    );

    const closeUsageModal = React.useCallback(() => {
        setShowUsage(null);
    }, []);

    // Redeems pagination handlers
    const setRedeemsPage = React.useCallback(
        (page: number) => {
            if (showUsage) {
                fetchPromoCodeRedeems(showUsage.id, page, redeemsPagination?.size || 10, redeemsSearchText);
            }
        },
        [showUsage, fetchPromoCodeRedeems, redeemsPagination?.size, redeemsSearchText]
    );

    const setRedeemsSize = React.useCallback(
        (size: number) => {
            if (showUsage) {
                fetchPromoCodeRedeems(showUsage.id, 1, size, redeemsSearchText);
            }
        },
        [showUsage, fetchPromoCodeRedeems, redeemsSearchText]
    );

    // Redeems search handler
    const handleRedeemsSearchChange = useDebouncedCallback((searchText: string) => {
        setRedeemsSearchText(searchText);
        if (showUsage) {
            // Reset to page 1 when searching
            fetchPromoCodeRedeems(showUsage.id, 1, redeemsPagination?.size || 10, searchText);
        }
    }, 500);

    return (
        <PromoCodesContext.Provider
            value={{
                promoCodes: currentPagePromoCodes,
                redeems,
                isLoading: loading.promoCodes,
                isFirstRender: loading.initialLoading,
                error,
                hasError: !!error,
                status,

                size: pageInfo.pageSize,
                page: pageInfo.page,
                totalPages: pagination?.totalPages ?? 1,
                hasMorePages,
                setSize: (size: number) => setPageInfo(prev => ({ ...prev, pageSize: size, page: 1 })),
                setPage: (page: number) => setPageInfo(prev => ({ ...prev, page })),

                redeemsSize: redeemsPagination?.size ?? 10,
                redeemsPage: redeemsPagination?.currentPage ?? 1,
                redeemsTotalPages: redeemsPagination?.totalPages ?? 1,
                redeemsHasMorePages,
                setRedeemsSize,
                setRedeemsPage,

                redeemsSearchText,
                setRedeemsSearchText: handleRedeemsSearchChange,

                selectedFilter: pageInfo.status,
                filterChangeHandler,
                searchText: pageInfo.searchText,
                searchTextChangeHandler,

                showAddPromoCode,
                editPromoCode,
                isConfigurationLocked,
                openAddForm,
                openEditForm,
                closeForm,

                savePromoCode,
                activatePromoCode,
                deactivatePromoCode,
                loadMorePromoCodes,
                refreshPromoCodes,
                clearError,
                clearSuccess,

                showUsage,
                setShowUsage,
                openUsageModal,
                closeUsageModal,

                fetchPromoCodeRedeems
            }}
        >
            {children}
        </PromoCodesContext.Provider>
    );
};

export const usePromoCodes = (): PromoCodesContextProps => {
    const context = React.useContext(PromoCodesContext);

    if (!context) {
        throw new Error('usePromoCodes must be used within a PromoCodesProvider');
    }

    return context;
};
